/**
 * bootstrap.js — Mode detection + data loading orchestrator
 *
 * URL patterns:
 *   /edit/:presetId                  → Edit mode (requires Supabase auth session)
 *   /hologram-editor/edit/:presetId  → Edit mode (via admin proxy)
 *   /d/:token                        → Display mode (public token URL, zero-interaction auto-play)
 *   /                                → Landing/redirect
 */
import { createClient } from './dataLoader.js';
import {
  loadPresetById,
  loadPresetByToken,
  getSessionUser,
} from './dataLoader.js';
import { adaptCircuitForHologram } from './circuitAdapter.js';

/**
 * @typedef {'edit' | 'display'} AppMode
 *
 * @typedef {{
 *   mode: AppMode;
 *   presetId: string | null;
 *   token: string | null;
 *   preset: object | null;
 *   circuit: object | null;
 *   drivers: object[];
 *   organization: object | null;
 *   user: object | null;
 *   error: string | null;
 * }} BootstrapResult
 */

/** Parse the URL to determine the app mode and extract IDs */
function parseUrl() {
  const path = window.location.pathname;

  // /edit/:presetId (standalone)
  const editMatch = path.match(/^\/edit\/([a-f0-9-]+)$/i);
  if (editMatch) {
    return { mode: /** @type {const} */ ('edit'), presetId: editMatch[1], token: null };
  }

  // /hologram-editor/edit/:presetId (admin proxy — F1)
  const proxyEditMatch = path.match(/^\/hologram-editor\/edit\/([a-f0-9-]+)$/i);
  if (proxyEditMatch) {
    return { mode: /** @type {const} */ ('edit'), presetId: proxyEditMatch[1], token: null };
  }

  // /d/:token — namespaced display route (F2)
  const tokenMatch = path.match(/^\/d\/([a-f0-9]+)$/i);
  if (tokenMatch) {
    return { mode: /** @type {const} */ ('display'), presetId: null, token: tokenMatch[1] };
  }

  // Root or unknown
  return { mode: /** @type {const} */ ('display'), presetId: null, token: null };
}

/**
 * Bootstrap the application — detect mode, load data from Supabase,
 * and return everything the main module needs to initialize.
 *
 * @returns {Promise<BootstrapResult>}
 */
export async function bootstrap() {
  const { mode, presetId, token } = parseUrl();

  /** @type {BootstrapResult} */
  const result = {
    mode,
    presetId,
    token,
    preset: null,
    circuit: null,
    drivers: [],
    organization: null,
    user: null,
    error: null,
  };

  try {
    if (mode === 'edit') {
      // Edit mode — require authentication
      const user = await getSessionUser();
      if (!user) {
        result.error = 'Authentication required. Please log in via the admin dashboard.';
        return result;
      }
      result.user = user;

      if (!presetId) {
        result.error = 'No preset ID specified in the URL.';
        return result;
      }

      const preset = await loadPresetById(presetId);
      if (!preset) {
        result.error = `Preset not found: ${presetId}`;
        return result;
      }

      result.preset = preset;

      // Load circuit data if the preset references one
      if (preset.circuit_id) {
        result.circuit = await loadCircuitData(preset.circuit_id);
      }

      // Load drivers from org_drivers
      if (preset.organization_id) {
        result.drivers = await loadOrgDrivers(preset.organization_id);
        result.organization = await loadOrganization(preset.organization_id);
      }
    } else if (mode === 'display' && token) {
      // Display mode — public access via token (F4: RPC with inline org branding)
      const preset = await loadPresetByToken(token);
      if (!preset) {
        result.error = 'Hologram not found or not published.';
        return result;
      }

      result.preset = preset;
      result.presetId = preset.id;

      // Org branding comes inline from the RPC (F4)
      result.organization = {
        name: preset.org_name,
        logo_url: preset.org_logo_url,
        primary_color: preset.org_primary_color,
        secondary_color: preset.org_secondary_color,
      };

      if (preset.circuit_id) {
        result.circuit = await loadCircuitData(preset.circuit_id);
      }

      // F6: Drivers via scoped RPC
      result.drivers = await loadOrgDriversPublic(preset.id);
    } else {
      result.error = 'Invalid URL. Use /edit/:presetId or /d/:token';
    }
  } catch (err) {
    console.error('[bootstrap] Failed to load data:', err);
    result.error = err instanceof Error ? err.message : 'Failed to load hologram data.';
  }

  return result;
}

/**
 * @param {string} circuitId
 */
async function loadCircuitData(circuitId) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('circuits')
    .select('*')
    .eq('id', circuitId)
    .single();

  if (error || !data) {
    console.warn('[bootstrap] Could not load circuit:', error?.message);
    return null;
  }

  return adaptCircuitForHologram(data);
}

/**
 * @param {string} organizationId
 */
async function loadOrgDrivers(organizationId) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('org_drivers')
    .select('*')
    .eq('organization_id', organizationId)
    .order('display_order', { ascending: true });

  if (error || !data) {
    console.warn('[bootstrap] Could not load drivers:', error?.message);
    return [];
  }

  return data.map((d) => ({
    id: d.id,
    firstName: d.first_name || '',
    lastName: d.last_name || '',
    profileImage: d.half_body_image_url || d.profile_image_url || '',
    numberImage: '',
  }));
}

/**
 * F6: Load only assigned drivers via scoped RPC (display mode).
 * @param {string} presetId
 */
async function loadOrgDriversPublic(presetId) {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('get_hologram_drivers', {
    p_preset_id: presetId,
  });
  if (error || !data) {
    console.warn('[bootstrap] Could not load drivers:', error?.message);
    return [];
  }
  return data.map((d) => ({
    id: d.id,
    firstName: d.first_name || '',
    lastName: d.last_name || '',
    profileImage: d.half_body_image_url || d.profile_image_url || '',
    numberImage: '',
  }));
}

/**
 * @param {string} organizationId
 */
async function loadOrganization(organizationId) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('organizations')
    .select('id, name, branding')
    .eq('id', organizationId)
    .single();

  if (error || !data) {
    console.warn('[bootstrap] Could not load organization:', error?.message);
    return null;
  }

  const branding = data.branding || {};
  return {
    id: data.id,
    name: data.name,
    logo_url: branding.logoUrl || null,
    primary_color: branding.primaryColor || null,
    secondary_color: branding.secondaryColor || null,
  };
}
